import { transport } from "@/src/config/apiConfig.js";

// v4 billing: credit wallet model — no plans, no seats.
// Users purchase credits (purchased_agent_credits column in billing_accounts) either one-time or recurring.
// Credits accumulate across purchases (each new purchase is added to the existing balance).
const BillingServiceV4 = {
  // Onboard workspace — creates billing account and grants initial credits from plan
  async onboard() {
    try {
      const response = await transport.post("/api/billing/v4/onboard");
      return response.data;
    } catch (error) {
      console.error("Error onboarding V4:", error);
      throw error;
    }
  },

  // Get or create billing account for v4 workspace
  async getBillingAccount() {
    try {
      const response = await transport.get("/api/billing/v4/account");
      return response.data;
    } catch (error) {
      console.error("Error getting billing account V4:", error);
      throw error;
    }
  },

  // Get current wallet balance (purchased_agent_credits in billing_accounts)
  // returns: { success, additional_credits, is_recurring }
  async getCredits() {
    try {
      const response = await transport.get("/api/billing/v4/credits");
      return response.data;
    } catch (error) {
      console.error("Error getting credits V4:", error);
      throw error;
    }
  },

  // Get billing summary (usage, transaction history, etc.)
  async getBillingSummary() {
    try {
      const response = await transport.get("/api/billing/v4/summary");
      return response.data;
    } catch (error) {
      console.error("Error getting billing summary V4:", error);
      throw error;
    }
  },

  // Create a checkout session to purchase credits.
  // payload: { credit_count: number, ui_mode: string, return_url: string }
  // Credits are added to the purchased_agent_credits balance on success.
  async createCheckoutSession(payload) {
    try {
      const response = await transport.post(
        "/api/billing/v4/checkout",
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating checkout session V4:", error);
      throw error;
    }
  },

  // Cancel an active recurring credit subscription
  async cancelRecurring() {
    try {
      const response = await transport.delete("/api/billing/v4/recurring");
      return response.data;
    } catch (error) {
      console.error("Error cancelling recurring V4:", error);
      throw error;
    }
  },

  // Check if the workspace has enough credits for an operation
  // payload: { credits_needed: number }
  async checkCreditsAvailable(payload) {
    try {
      const response = await transport.post(
        "/api/billing/v4/credits/check",
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error checking credits available V4:", error);
      throw error;
    }
  },
  // Get active credit packs for the current workspace's currency
  async getCreditPacks() {
    try {
      const response = await transport.get("/api/billing/v4/credit_packs");
      return response.data;
    } catch (error) {
      console.error("Error getting credit packs V4:", error);
      throw error;
    }
  },

  // List all per-user credit budgets for the current workspace.
  // Absence from this list = unlimited (default).
  async listUserBudgets() {
    try {
      const response = await transport.get("/api/billing/v4/user_budgets");
      return response.data;
    } catch (error) {
      console.error("Error listing user budgets V4:", error);
      throw error;
    }
  },

  // Upsert a user's monthly credit budget.
  // payload: { monthly_limit_credits: number, is_enabled: boolean }
  // Backend rejects is_enabled=true with non-positive limit (400).
  async upsertUserBudget(authUserId, payload) {
    try {
      const response = await transport.put(
        `/api/billing/v4/user_budgets/${authUserId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error upserting user budget V4:", error);
      throw error;
    }
  },

  // Remove a user's budget row (= unlimited). Called on user removal from workspace.
  async deleteUserBudget(authUserId) {
    try {
      const response = await transport.delete(
        `/api/billing/v4/user_budgets/${authUserId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting user budget V4:", error);
      throw error;
    }
  },

  // Verify a Razorpay payment after checkout
  async verifyRazorpayPayment(paymentData) {
    try {
      const response = await transport.post(
        "/api/billing/v4/verify_razorpay_payment",
        paymentData,
      );
      return response.data;
    } catch (error) {
      console.error("Error verifying Razorpay payment V4:", error);
      throw error;
    }
  },

  // Activate a Razorpay subscription after the first payment
  async activateRazorpaySubscription(paymentData) {
    try {
      const response = await transport.post(
        "/api/billing/v4/activate_razorpay_subscription",
        paymentData,
      );
      return response.data;
    } catch (error) {
      console.error("Error activating Razorpay subscription V4:", error);
      throw error;
    }
  },
};

export default BillingServiceV4;
